/**
 * src/utils/lazyWithTimeout.js
 *
 * React.lazy, except a chunk fetch that never settles cannot strand the UI.
 *
 * WHY THIS EXISTS
 *
 * Every route page and every modal in this app is code-split behind
 * `React.lazy`, and each one renders inside a `<Suspense>` whose fallback is
 * deliberately near-empty — an empty div for a route, a bare
 * `.modal-backdrop` for a modal. That is the right fallback for the case it
 * was written for: a chunk that arrives in a few hundred milliseconds.
 *
 * It is the wrong fallback for a chunk that never arrives. The app does not
 * serve its own static assets — the document, the bundles and every chunk come
 * from Azure Static Web Apps through the Cloudflare `seasuite-proxy` Worker,
 * and that leg intermittently stalls (PLAYSCRIPT-LEARNINGS.md PL-249, and the
 * four edge failures catalogued in playwright-guides/edge-tail.js). A stalled
 * fetch does not reject. The `import()` promise simply stays pending, so
 * Suspense sits on its fallback indefinitely and the user gets a blank page or
 * a dimmed screen with no modal in it, with no error, no spinner and no way
 * out but a manual reload.
 *
 * ErrorBoundary.jsx already recovers from a chunk that REJECTS — it matches on
 * the browser's "Failed to fetch dynamically imported module" message and
 * reloads, which fixes the stale-chunk-after-deploy case. A pending promise
 * never reaches it. This module is what turns the second case into the first.
 *
 * WHY THIS DOES NOT RETRY THE IMPORT IN PLACE
 *
 * The obvious implementation — time out, call the loader again — does nothing,
 * and would be worse than nothing because it would look like a fix. A module
 * specifier that is already being fetched is deduplicated by the browser's
 * module map: a second `import()` for the same specifier returns the SAME
 * pending promise, not a new request. There is no way to bust that from here,
 * because Vite rewrites the specifier to a hashed URL at build time and the
 * call site cannot append a cache-buster to it.
 *
 * So a stall is escalated rather than retried: reject with the phrase the
 * ErrorBoundary already acts on, and let the page reload rebuild the module
 * map from scratch. One reload beats an indefinite blank screen.
 *
 * Note this fixes the symptom's own layer, not the layer beneath it. The
 * Worker → Static Web Apps leg still stalls; the user is no longer stranded
 * when it does.
 */
import { lazy } from 'react';

/**
 * How long a chunk may take before it is treated as stalled.
 *
 * Deliberately generous. The largest chunks in this app are substantial
 * (DocumentEditor.jsx ~248 KB, EmployeeHub.jsx ~148 KB) and a slow mobile
 * connection must not be mistaken for a stall — the cost of being wrong here
 * is a full page reload, so the bar is "plainly not coming", not "slow".
 * PL-249 recorded a 522 arriving 19 s after the request, so the ceiling is
 * below that: waiting for the edge's own error is waiting too long.
 */
export const CHUNK_TIMEOUT_MS = 15_000;

/**
 * The ErrorBoundary matches on this substring to decide a chunk failure is
 * recoverable by reload. Keep the phrase byte-identical to the browser's own
 * message or the recovery silently stops firing — the whole point of this
 * module is to route a stall into that existing path.
 */
const TIMEOUT_MESSAGE =
  'Failed to fetch dynamically imported module — no response within ' +
  `${CHUNK_TIMEOUT_MS / 1000}s`;

/**
 * Settle `promise`, or reject once `timeoutMs` has passed with no answer.
 *
 * The underlying fetch is not cancelled — a dynamic import cannot be aborted.
 * It is abandoned: if it does eventually arrive it populates the module map,
 * so the reload this rejection triggers finds it already there.
 */
export function withTimeout(promise, timeoutMs = CHUNK_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(TIMEOUT_MESSAGE));
    }, timeoutMs);

    promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * Drop-in replacement for `React.lazy`. Same signature, same caching
 * semantics — React calls the factory once and holds the result.
 */
export function lazyWithTimeout(loader, timeoutMs = CHUNK_TIMEOUT_MS) {
  return lazy(() => withTimeout(loader(), timeoutMs));
}

export default lazyWithTimeout;
